// What both in-memory twins share for M2-04. Chat mode's fake (fake-chat.ts, M2-06) and Work
// mode's fake (fake-work.ts, M2-07) share the company's spaces (fake.ts `twinRoutes`), but each
// keeps its own inbox items and searchable rows: those lists are merged here (both twins' rows,
// newest first), and the inbox filter and search match rules are the same in both.
import type { Ctx, Handler } from "./fake";

/** A fake handler that may need to read another handler's body first. */
export type ComposedHandler = (ctx: Ctx) => Response | Promise<Response>;

type Maybe = Handler | undefined;

type Page = { data: { id?: string; created_at?: string }[]; next_cursor: string | null };

/**
 * Both twins' pages as one: rows deduplicated by id, newest first when `byNewest`, cut to the
 * request's limit. An error from either twin is answered as is.
 */
export function mergedPages(byNewest: boolean, ...hs: Maybe[]): ComposedHandler {
  return async (ctx) => {
    const pages: Page[] = [];
    for (const h of hs) {
      if (!h) continue;
      const res = h(ctx);
      if (!res.ok) return res;
      pages.push((await res.json()) as Page);
    }
    const seen = new Set<string>();
    let data = pages
      .flatMap((p) => p.data)
      .filter((r) => {
        if (!r.id) return true;
        if (seen.has(r.id)) return false;
        seen.add(r.id);
        return true;
      });
    if (byNewest)
      data = data.sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
    const limit = Math.min(200, Number(ctx.url.searchParams.get("limit") ?? 50) || 50);
    return Response.json({
      data: data.slice(0, limit),
      next_cursor: pages.find((p) => p.next_cursor)?.next_cursor ?? null,
      prev_cursor: null,
    });
  };
}

/** Every twin applies the write; the counts add up (`inbox.readAll`). */
export function summedCounts(...hs: Maybe[]): ComposedHandler {
  return async (ctx) => {
    let count = 0;
    for (const h of hs) {
      if (!h) continue;
      const res = h(ctx);
      if (!res.ok) return res;
      count += ((await res.json()) as { count: number }).count;
    }
    return Response.json({ count });
  };
}

/** M2-04's inbox list rule, for both twins: the live list, or `done` / `snoozed` items. */
export function inboxMatches(
  i: {
    kind: string;
    read_at: string | null;
    done_at: string | null;
    snoozed_until: string | null;
    category?: string | null;
  },
  filter: string | null | undefined,
  now: string,
): boolean {
  if (filter === "done") return i.done_at !== null;
  const snoozed = i.snoozed_until !== null && i.snoozed_until > now;
  if (filter === "snoozed") return i.done_at === null && snoozed;
  if (i.done_at !== null || snoozed) return false;
  // INBOX-PREFS: Waiting on me (approvals, a DE's question) and DMs.
  if (filter === "waiting") return i.kind === "approval" || i.kind === "agent_escalation";
  if (filter === "dms") return i.category === "dms";
  if (filter === "unread") return i.read_at === null;
  // As the API: a mention in a DM sits under DMs.
  if (filter === "mentions") return i.kind === "mention" && i.category !== "dms";
  if (filter === "approvals") return i.kind === "approval";
  if (filter === "followed") return i.kind === "reply" || i.kind === "task_change";
  if (filter === "memory") return i.kind === "memory";
  return true;
}

export const INBOX_FILTERS = [
  "waiting",
  "dms",
  "unread",
  "approvals",
  "mentions",
  "followed",
  "memory",
  "done",
  "snoozed",
];

/** `GET search`'s rule in the twins: every word of `q` starts a word of `text` (prefix FTS). */
export function textMatches(q: string, text: string): boolean {
  const words = q.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];
  if (words.length === 0) return false;
  const tokens = text.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];
  return words.every((w) => tokens.some((t) => t.startsWith(w)));
}
